import invariant from 'tiny-invariant'
import { v4 as uuidv4 } from 'uuid'

import type { definitions } from '@luxuryescapes/contract-svc-accommodation'

import { getOfferInclusionDescriptionFromMarkdown } from 'api/mappers/offerInclusionMap'
import config from 'constants/config'
import { EmptyArray, sortBy } from 'lib/array/arrayUtils'
import { mapObject } from 'lib/object/objectUtils'
import determineOfferFeatureSymbol from 'lib/offer/determineOfferFeatureSymbol'
import getValueOffPercent from 'lib/offer/getValueOffPercent'
import { assertUnreachable } from 'lib/staticAssert/staticAssertUtils'

type ServerAvailabilityResponseProperty = definitions['AvailabilityResponseProperty']
type ServerUnifiedRate = definitions['UnifiedRate']

type ServerMetaKey = ServerUnifiedRate['meta']
type ServerLPCMetaKey = definitions['LPCMetaKey']
type ServerLPPMetaKey = definitions['LPPMetaKey']
type ServerRateType = ServerMetaKey['type']

type ServerBoardCode = ServerUnifiedRate['boardCode']
type ServerBundle =
  | NonNullable<ServerLPCMetaKey['bundle']>
  | NonNullable<ServerLPPMetaKey['bundle']>
type ServerCancellationPolicy = definitions['CancellationPolicies']
type ServerInclusion = definitions['Inclusion']
type ServerPaymentSchedule =
  | NonNullable<ServerLPCMetaKey['paymentSchedule']>
  | NonNullable<ServerLPPMetaKey['paymentSchedule']>
type ServerPromotion = definitions['Promotion']
type ServerRateTotals = definitions['RateTotals']
type ServerRefundableStatus = definitions['RefundableStatus']

function isLpcMeta(meta: ServerMetaKey): meta is ServerLPCMetaKey {
  return meta.type === 'reservation'
}

function isBedbankMeta(meta: ServerMetaKey): meta is ServerLPPMetaKey {
  return meta.type === 'bedbank'
}

function mapInclusionDescription(
  serverRateType: ServerRateType,
  text: string,
): string {
  switch (serverRateType) {
    case 'reservation':
      return getOfferInclusionDescriptionFromMarkdown(text)
    case 'bedbank':
      return text
    default:
      assertUnreachable(serverRateType)
  }
}

function mapBoardCode(serverBoardCode: ServerBoardCode): App.RateBoardCode {
  switch (serverBoardCode) {
    case 'AI':
      return 'allinclusive'
    case 'BB':
      return 'breakfast'
    case 'FB':
      return 'fullboard'
    case 'HB':
      return 'halfboard'
    case 'RO':
      return 'roomonly'
  }
}

function mapBundle({
  experienceId,
  productId,
  bundleId,
}: ServerBundle): App.Property.Bundle | undefined {
  if (!(experienceId && productId && bundleId)) return undefined
  return {
    bundleId,
    experienceId,
    productId,
  }
}

export function mapInclusion(
  serverRateType: ServerRateType,
  { text, icon }: ServerInclusion,
  inclusionType: 'normal' | 'bonus' | 'luxPlus',
): App.OfferInclusion {
  const description = mapInclusionDescription(serverRateType, text)
  return {
    id: uuidv4(),
    description,
    symbol: determineOfferFeatureSymbol(icon || description),
    isBonus: inclusionType === 'bonus',
    isLuxPlusBonus: inclusionType === 'luxPlus',
  }
}

function mapPaymentMethod({
  paymentMethod,
  payAtProperty,
}: Pick<ServerLPPMetaKey, 'paymentMethod' | 'payAtProperty'>, currency: string): App.Property.PaymentMethod {
  // TODO: Make this field non-optional in accommodation service.
  switch (paymentMethod ?? 'leCollect') {
    case 'leCollect':
      return { type: 'leCollect' }
    case 'propertyCollect':
      return {
        type: 'propertyCollect',
        totals: payAtProperty && payAtProperty.currencyCode !== currency ? {
          displayPrice: payAtProperty.displayPrice,
          currency: payAtProperty.currencyCode,
        } : undefined,
      }
  }
}

function mapPaymentScheduleInfo({
  templateId,
  supplierDueDays,
}: ServerPaymentSchedule): App.PaymentScheduleInfo {
  return {
    staticSchedule: {
      templateId,
      supplierPaymentNumberOfDays: supplierDueDays,
    },
  }
}

export function mapPromotion({
  name,
  travelPeriod: {
    from: travelFromIsoDate,
    to: travelToIsoDate,
  },
  minimumLos,
}: ServerPromotion): App.Property.Promotion {
  return {
    name,
    travelFromIsoDate,
    travelToIsoDate,
    minimumLos,
  }
}

function mapCancellationPolicy({
  amount,
  description,
  start,
  end,
  nonRefundable,
}: ServerCancellationPolicy): App.Property.CancellationPolicy {
  return {
    startIsoDateTime: start,
    endIsoDateTime: end,
    penaltyPrice: amount,
    isFullyNonRefundable: nonRefundable,
    originalDescription: description,
  }
}

function mapHeadlineCancellationPolicy(
  serverRefundableStatus: ServerRefundableStatus,
  serverFullyRefundableUntil: string | undefined,
  serverPartiallyRefundableUntil: string | undefined,
): ['fullyRefundable' | 'partiallyRefundable', string] | ['nonRefundable', undefined] {
  switch (serverRefundableStatus) {
    case 'refundable':
      return ['fullyRefundable', serverFullyRefundableUntil!]
    case 'partially':
      return ['partiallyRefundable', serverPartiallyRefundableUntil!]
    case 'non-refundable':
      return ['nonRefundable', undefined]
    default:
      return assertUnreachable(serverRefundableStatus)
  }
}

export function mapRateTotals(
  serverRateTotals: ServerRateTotals,
): App.PropertyRateTotals {
  return {
    ...serverRateTotals,
    // TODO: Incorporate into accommodation service
    societeHotelPrice: serverRateTotals.displayPrice - serverRateTotals.propertyFees,
    // TODO(HP-1274): Use server's `discount` field once it's trustworthy.
    discount: getValueOffPercent(serverRateTotals.valuedUpToPrice, serverRateTotals.displayPrice),
  }
}

function mapPropertyRate(serverRate: ServerUnifiedRate, currency: string): App.RawPropertyRate {
  const serverRateMeta = serverRate.meta
  // NB: These fields were last audited against v6.10.0 of `contract-svc-accommodation`.
  const [headlineCancellationPolicyType, headlineCancellationPolicyStart] = mapHeadlineCancellationPolicy(serverRate.refundableStatus, serverRate.fullRefundableUntil, serverRate.partiallyRefundableUntil)
  const baseRate: Omit<App.RawPropertyRate, 'service' | 'exceedsIncludedGuests' | 'hasAppliedDiscount' | 'isMobilePromotion' | 'paymentScheduleInfo' | 'name' | 'paymentMethod' | 'relations'> = {
    /* Identifiers */
    propertyId: serverRate.propertyId,
    roomId: serverRate.roomId,
    rateId: serverRate.id,
    /* Deduplication keys */
    groupKey: serverRate.groupKey,
    bundlingKey: serverRate.bundlingKey,
    isFlightBundle: !!serverRateMeta.isFlightBundle,
    boardCode: mapBoardCode(serverRate.boardCode),
    /* Details */
    currency,
    /* Content */
    staffLabel: serverRate.staffLabel,
    /* Inclusions */
    promotion: serverRate.promotion ? mapPromotion(serverRate.promotion) : undefined,
    inclusions: serverRate.inclusions.map(serverInclusion => mapInclusion(serverRate.meta.type, serverInclusion, 'normal')),
    bonusInclusions: serverRate.bonusInclusions.map(serverInclusion => mapInclusion(serverRate.meta.type, serverInclusion, 'bonus')),
    luxPlusInclusions: serverRate.luxPlusInclusions.map(serverInclusion => mapInclusion(serverRate.meta.type, serverInclusion, 'luxPlus')),
    bundle: serverRateMeta.bundle ? mapBundle(serverRateMeta.bundle) : undefined,
    bundleInclusions: serverRate.bundleInclusions.map(serverInclusion => mapInclusion(serverRate.meta.type, serverInclusion, 'normal')),
    /* Cancellation policies */
    cancellationPolicies: serverRate.cancellationPolicies.map(mapCancellationPolicy),
    /* Pricing */
    totals: mapRateTotals(serverRate.total),
    // TODO(Xiao): Guard behind relevant feature flags:
    //   - `config.LUXURY_PLUS_ENABLED`;
    //   - `OptimizelyFeatureFlags.luxPlusFeatureToggle`; and
    //   - `OptimizelyFeatureFlags.luxPlusLppPricingEnabled`.
    luxPlusTotals: serverRate.luxPlus && mapRateTotals(serverRate.luxPlus),
    /* Metadata */
    availableRooms: serverRate.availableRooms ?? 1,
    mergedRates: serverRateMeta.mergedRates ?? EmptyArray,
    /* (Irrelevant) */
    // `serverRate.occupancies` is not set.
    // `serverRate.margin` is filtered out for non-admins.
    // `serverRate.groupScore` is filtered out for non-admins.
    /* TODO: Audit, document, tighten contract */
    fullyRefundableUntil: serverRate.fullRefundableUntil,
    headlineCancellationPolicyType,
    headlineCancellationPolicyStart,
  }
  if (isLpcMeta(serverRateMeta)) {
    return {
      ...baseRate,
      /* Details */
      service: {
        /* Identifiers */
        type: 'lpc',
        offerId: serverRateMeta.offerId,
        propertyId: serverRateMeta.reservationPropertyId,
        packageId: serverRateMeta.packageId ?? serverRateMeta.lePackageId,
        roomId: serverRateMeta.reservationRoomTypeId,
        rateId: serverRateMeta.reservationRoomRateId,
        /* Status flags */
        isTactical: config.LPC_TACTICAL && serverRateMeta.isTactical,
        isAutoLuxPlus: !!serverRateMeta.isAutoLuxplus,
      },
      /* Status flags */
      exceedsIncludedGuests: !!serverRateMeta.exceedsIncludedGuests,
      hasAppliedDiscount: !!serverRateMeta.hasAppliedDiscount,
      isMobilePromotion: false,
      /* Configuration options */
      paymentScheduleInfo: config.PAYMENT_SCHEDULE_HOTEL_ENABLED && serverRateMeta.paymentSchedule
        ? mapPaymentScheduleInfo(serverRateMeta.paymentSchedule)
        : undefined,
      /* Content */
      name: serverRateMeta.name,
      /* Pricing */
      paymentMethod: {
        // For now, LPC rates can only ever be LE collect.
        type: 'leCollect',
      },
      /* Metadata */
      relations: {
        // We don't expect accommodation service to return self-references, but it doesn't hurt to
        // verify this assumption on our side.
        paymentMethod: {
          leCollect: [],
          propertyCollect: serverRate.relations?.paymentMethod
            ? serverRate.relations.paymentMethod.flatMap(({ rateId, value }) => {
                if (value === 'propertyCollect' && rateId !== serverRate.id) return [rateId]
                return []
              })
            : [],
        },
      },
    }
  } else if (isBedbankMeta(serverRateMeta)) {
    return {
      ...baseRate,
      /* Details */
      service: {
        /* Identifiers */
        type: 'bedbank',
        supplier: serverRateMeta.supplier,
        supplierCode: serverRateMeta.supplierCode!, // This _should_ be always set.
        propertyId: serverRateMeta.bedbankPropertyId,
        roomId: serverRateMeta.bedbankRoomId,
        rateId: serverRateMeta.bedbankRateId,
        /* Deduplication keys */
        rateGroupId: serverRateMeta.rateGroupId,
        /* Status flags */
        pricingMethod: serverRateMeta.pricingMethod,
        /* Configuration options */
        bedGroups: serverRateMeta.bedGroups ?? EmptyArray,
      },
      /* Status flags */
      exceedsIncludedGuests: false,
      hasAppliedDiscount: false,
      isMobilePromotion: !!serverRateMeta.isMobilePromotion,
      /* Configuration options */
      paymentScheduleInfo: config.PAYMENT_SCHEDULE_LPP_ENABLED && serverRateMeta.paymentSchedule
        ? mapPaymentScheduleInfo(serverRateMeta.paymentSchedule)
        : undefined,
      /* Content */
      name: undefined,
      /* Pricing */
      paymentMethod: mapPaymentMethod(serverRateMeta, currency),
      /* Metadata */
      relations: {
        // We don't expect accommodation service to return self-references, but it doesn't hurt to
        // verify this assumption on our side.
        paymentMethod: {
          leCollect: (serverRateMeta.paymentMethod !== 'leCollect' && serverRate.relations?.paymentMethod)
            ? serverRate.relations.paymentMethod.flatMap(({ rateId, value }) => {
                if (value === 'leCollect' && rateId !== serverRate.id) return [rateId]
                return []
              })
            : [],
          propertyCollect: (serverRateMeta.paymentMethod !== 'propertyCollect' && serverRate.relations?.paymentMethod)
            ? serverRate.relations.paymentMethod.flatMap(({ rateId, value }) => {
                if (value === 'propertyCollect' && rateId !== serverRate.id) return [rateId]
                return []
              })
            : [],
        },
      },
    }
  }
  assertUnreachable(serverRateMeta)
}

export default function mapPropertyRates({
  rooms: serverRooms,
}: ServerAvailabilityResponseProperty, currency: string): Array<App.RawPropertyRoomRates> {
  // NB: Currently, accommodation service gives us _one_ ordering for rooms. Theoretically, the
  // rooms might be ordered differently between hotel-only and hotel-with-flights views. However,
  // this _should_ be rare; we'll cross that bridge when we come to it.
  const sortedServerRooms = sortBy(Object.entries(serverRooms), ([_, { sort }]) => sort, 'asc')
  const rooms = sortedServerRooms.map(([roomId, serverRoom]) => {
    const serverUnavailableReason = serverRoom.unavailableReason
    if (serverUnavailableReason) {
      if (serverUnavailableReason === 'FILTERED_OUT') {
        invariant(false, 'Accommodation returned FILTERED_OUT for property room rates')
      }
      return {
        roomId,
        unavailableReason: serverUnavailableReason,
      }
    }
    const rates = mapObject(serverRoom.ratesMap, serverRate => mapPropertyRate(serverRate, currency))
    return {
      roomId,
      rates,
      lists: {
        hotelOnly: serverRoom.hotelOnlyRates,
        hotelWithFlights: config.FLIGHT_ENABLED ? serverRoom.hotelWithFlightRates : [],
      },
    }
  })
  return rooms.length ? rooms : EmptyArray
}
